// PictureDrop — a picture you can DROP onto a row (or click to choose), and ✕ to
// remove. Alex, 2026-10-15, on the project logo (COS-5): "it easy to drag and drop
// a logo in a, a picture in a row?" → "OK for the picture drop".
//
// This reverses one half of the 2026-07-21 rule ("picture will not be added through
// the UI") for the item it is placed on — today only the project logo (`proj:<id>`).
// Showing stays ImageCell's job: this wraps it, so a dropped picture renders exactly
// like every other stored picture (hover to enlarge included).
//
// WHERE THE PICTURE GOES: a small JPEG/PNG data URL in item_images, through the
// existing PUT /api/item-images/:dbId. Not a file on disk — Render's disk does not
// survive a deploy, the database does. The browser shrinks it first (image-shrink.js,
// the same redraw the chat uses for pasted pictures) to a thumbnail that fits the
// route's 64 KB body limit; a logo is shown at 28-40 px, so 256 px is already 6x.
//
// PictureDrop({ dbId, size, what })
//   dbId — the item_images key, e.g. 'proj:9'
//   size — the picture's box in px (default 28)
//   natural / maxWidth — keep the picture's own shape (a wide logo fills up to maxWidth)
//   what — the word used in the hints, e.g. 'logo' (default 'picture')
//
// Clicks and double-clicks stop here: the cell sits inside rows whose click edits
// and whose double-click opens, and choosing a file must do neither.
import { useRef, useState } from 'react';
import ImageCell, { useItemImages, invalidateItemImages } from './ImageCell.jsx';
import Notice from './Notice.jsx';
import { DELETE } from '../icons.js';
import { BTN_GLYPH } from '../theme-tokens.js';
import { shrinkImage } from '../image-shrink.js';

const MAX_EDGE = 256;
const BODY_LIMIT = 60_000;   // the PUT route's JSON limit is 64 KB; leave room for the envelope

// Shrink until the data URL fits the route, giving up after a smaller, plainer try.
async function toThumbnailUrl(file) {
  for (const opts of [{ maxEdge: MAX_EDGE, quality: 0.85 }, { maxEdge: 160, quality: 0.7 }]) {
    const img = await shrinkImage(file, { ...opts, keepBytes: 30_000 });
    const url = `data:${img.media_type};base64,${img.data}`;
    if (url.length <= BODY_LIMIT) return url;
  }
  throw new Error('That picture is too detailed to store as a logo. Try a simpler or smaller file.');
}

const isImageDrag = e => [...(e.dataTransfer?.items || [])].some(i => i.kind === 'file' && i.type.startsWith('image/'));

export default function PictureDrop({ dbId, size = 28, what = 'picture', natural = false, maxWidth }) {
  const images = useItemImages();
  const has = !!(images && images.get(dbId));
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const save = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError(`That file is not a picture.`); return; }
    setBusy(true); setError(null);
    try {
      const url = await toThumbnailUrl(file);
      const r = await fetch(`/api/item-images/${encodeURIComponent(dbId)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Could not save the ${what}.`);
      invalidateItemImages();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const remove = async (e) => {
    e.stopPropagation();
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/item-images/${encodeURIComponent(dbId)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Could not remove the ${what}.`);
      invalidateItemImages();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const stop = e => e.stopPropagation();
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, minWidth: 0 }} onClick={stop} onDoubleClick={stop}>
      <span
        title={busy ? `Saving the ${what}…` : has ? `Drop a picture to replace the ${what}, or click to choose one` : `Drop a ${what} here, or click to choose one`}
        onDragOver={e => { if (isImageDrag(e)) { e.preventDefault(); setOver(true); } }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); save(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer',
          minWidth: size, minHeight: size, borderRadius: 2,
          outline: over ? '2px dashed var(--th-accent)' : 'none', outlineOffset: 1,
          opacity: busy ? 0.5 : 1,
        }}
      >
        {has
          ? <ImageCell dbId={dbId} size={size} natural={natural} maxWidth={maxWidth} />
          : <span style={{
              width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              border: '1px dashed var(--th-border-subtle)', borderRadius: 2, color: 'var(--th-text-faint)',
            }}>+</span>}
        {has && !busy && (
          <button onClick={remove} title={`Remove the ${what}`} aria-label={`Remove the ${what}`}
                  style={{ ...BTN_GLYPH, color: 'var(--th-text-faint)', lineHeight: 1 }}>{DELETE}</button>
        )}
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
               onChange={e => { save(e.target.files?.[0]); e.target.value = ''; }} />
      </span>
      {error && <Notice inline onDismiss={() => setError(null)}>{error}</Notice>}
    </span>
  );
}
